import { useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import useAuthStore from '../store/authStore';
import { ROLES, roleLabels } from '../utils/roles';
import { useLocation, useNavigate } from 'react-router-dom';
import ParabolaLogo from '../components/ParabolaLogo';

const drawerWidth = 250;

const BRAND = {
  primary: '#4357AD',
  green: '#D3FEC6',
  orange: '#FF521B',
  dark: '#1E2A4A',
  bg: '#F3F0FF',
  sidebar: '#FFFFFF',
  activeItem: '#EDE9FE',
  activeBorder: '#4357AD',
  textMuted: '#8891A5',
};

const DashboardLayout = ({ navItems = [], children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const downMd = useMediaQuery('(max-width:900px)');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, role, logout } = useAuthStore();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const isSelected = (item) => {
    if (item.exact) return location.pathname === item.path;
    if (location.pathname === item.path) return true;
    if (location.pathname.startsWith(`${item.path}/`)) {
      // Ensure no other nav item is a more specific match
      return !navItems.some(
        (other) =>
          other.path !== item.path &&
          other.path.startsWith(`${item.path}/`) &&
          (location.pathname === other.path || location.pathname.startsWith(`${other.path}/`))
      );
    }
    return false;
  };

  const renderDrawer = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: BRAND.sidebar,
        borderRight: '1px solid #EDE9FE',
      }}
    >
      {/* Logo */}
      <Box sx={{ px: 2, pt: 2.5, pb: 1.5 }}>
        {!downMd && (
          <ParabolaLogo width={120} height={42} style={{ color: BRAND.dark, display: 'block' }} />
        )}
        <Box
          sx={{
            mt: 1,
            display: 'inline-flex',
            px: 1.5,
            py: 0.4,
            borderRadius: 999,
            bgcolor: BRAND.green,
            color: '#1A7A1A',
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: 0.3,
          }}
        >
          {roleLabels[role] || role}
        </Box>
      </Box>

      {/* Nav */}
      <List sx={{ flexGrow: 1, px: 1.5, pt: 1 }}>
        {navItems.map((item) => {
          const active = isSelected(item);
          return (
            <ListItemButton
              key={item.path}
              selected={active}
              onClick={() => {
                navigate(item.path);
                if (mobileOpen) setMobileOpen(false);
              }}
              sx={{
                borderRadius: 3,
                mb: 0.5,
                py: 1.2,
                color: active ? BRAND.primary : BRAND.dark,
                bgcolor: active ? BRAND.activeItem : 'transparent',
                fontWeight: active ? 700 : 500,
                borderLeft: active ? `3px solid ${BRAND.activeBorder}` : '3px solid transparent',
                '&:hover': {
                  bgcolor: active ? BRAND.activeItem : '#F8F7FF',
                },
                '&.Mui-selected': {
                  bgcolor: BRAND.activeItem,
                  '&:hover': { bgcolor: BRAND.activeItem },
                },
              }}
            >
              {item.icon && (
                <ListItemIcon sx={{ color: active ? BRAND.primary : BRAND.textMuted, minWidth: 38 }}>
                  {item.badge > 0 ? (
                    <Badge
                      badgeContent={item.badge}
                      color="error"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: 10,
                          height: 18,
                          minWidth: 18,
                          fontWeight: 700,
                        },
                      }}
                    >
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
              )}
              <ListItemText
                primary={item.label}
                secondary={item.subtitle || null}
                primaryTypographyProps={{ fontWeight: active ? 700 : 500, fontSize: 14 }}
                secondaryTypographyProps={{ fontSize: 11, color: active ? BRAND.primary : BRAND.textMuted, mt: 0.2 }}
              />
            </ListItemButton>
          );
        })}
      </List>

      {/* User info + Logout */}
      <Box sx={{ px: 2, pb: 3 }}>
        {user && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.5,
              py: 1.5,
              mb: 1.5,
              borderRadius: 3,
              bgcolor: '#F8F7FF',
              cursor: role === ROLES.TUTOR ? 'pointer' : 'default',
              '&:hover': role === ROLES.TUTOR ? { bgcolor: BRAND.activeItem } : {},
            }}
            onClick={role === ROLES.TUTOR ? () => navigate('/tutor/settings') : undefined}
          >
            <Avatar sx={{ bgcolor: BRAND.primary, width: 36, height: 36, fontSize: 14, fontWeight: 700 }}>
              {user.name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: BRAND.dark, lineHeight: 1.3 }} noWrap>
                {user.name}
              </Typography>
              <Typography variant="caption" sx={{ color: BRAND.textMuted }} noWrap>
                {user.email}
              </Typography>
            </Box>
          </Box>
        )}
        <Button
          fullWidth
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{
            borderRadius: 3,
            textTransform: 'none',
            color: BRAND.textMuted,
            justifyContent: 'flex-start',
            px: 2,
            '&:hover': { bgcolor: '#FEF2F2', color: BRAND.orange },
          }}
        >
          Вийти
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: BRAND.bg }}>
      {/* Top bar — mobile only for burger; desktop — just user info */}
      {downMd && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: 56,
            bgcolor: '#fff',
            zIndex: 1201,
            display: 'flex',
            alignItems: 'center',
            px: 2,
            borderBottom: '1px solid #EDE9FE',
          }}
        >
          <IconButton onClick={handleDrawerToggle} sx={{ color: BRAND.dark }}>
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, ml: 1, display: 'flex', alignItems: 'center' }}>
            <ParabolaLogo width={100} height={36} style={{ color: BRAND.dark }} />
          </Box>
          {user && (
            <Avatar sx={{ bgcolor: BRAND.primary, width: 34, height: 34, fontSize: 14, fontWeight: 700 }}>
              {user.name?.charAt(0)?.toUpperCase()}
            </Avatar>
          )}
        </Box>
      )}

      {/* Sidebar */}
      {navItems.length > 0 && (
        <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { width: drawerWidth, border: 'none' },
            }}
          >
            {renderDrawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', border: 'none' },
            }}
            open
          >
            {renderDrawer}
          </Drawer>
        </Box>
      )}

      {/* Main */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${navItems.length > 0 ? drawerWidth : 0}px)` },
          minWidth: 0,
          overflow: 'hidden',
          mt: { xs: '56px', md: 0 },
        }}
      >
        <Box sx={{ px: { xs: 1.5, sm: 2, md: 4 }, pb: 4, pt: { xs: 1, md: 3 } }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;
